// src/data/courses.js
//
// ===========================================================================
// WHY THIS FILE EXISTS: GRADE STOPS BEING THE UNIT AT 9TH
// ===========================================================================
// Everything in the portal is currently addressed as (state, grade, subject).
// One 5th-grade math, one 5th-grade science. That is exactly right for K-5,
// where one teacher covers every subject and every child in the room is on
// the same page of the same book.
//
// It stops being true partway up. In Virginia:
//
//   • History IS still grade-scoped all the way to 12. Grade 9 is World
//     History to 1500, grade 11 is Virginia & US History. Every 11th grader
//     takes the same course.
//
//   • Science and math are NOT. A 10th grader might be in Biology I or
//     Chemistry I; in Geometry, Algebra 2, or still finishing Algebra 1.
//     "10th grade math" names no actual course. Ask the state: VA publishes
//     its high-school math standards as Algebra 1, Geometry, Algebra 2,
//     Trigonometry, Data Science — a list of courses, with no grade attached
//     to any of them.
//
// So a student's science page can't be derived from their grade. It has to be
// derived from what they're enrolled in. This file is that layer.
//
// ===========================================================================
// THE TRACK TOKEN — HOW THIS FITS WITHOUT A ROUTE REWRITE
// ===========================================================================
// The portal route is already:
//
//     /portal/:state/:grade/:subject/:view   ->  portalLayouts/{state}-{grade}-{subject}
//
// `:grade` is just a string token. It does not have to be a grade.
//
// Call that slot the TRACK. A track token is whichever of these addresses
// the content:
//
//     grade token   "5th", "8th", "k"        — K-8, and 9-12 social studies
//     course token  "algebra-1", "biology-1" — 9-12 science and math
//
// So /portal/va/5th/math/student and /portal/va/algebra-1/math/student are
// the same route, the same component, the same Firestore shape. No new route,
// no new page, no branch in DynamicPortalPage beyond a nicer heading.
//
// The one hard rule this creates: A COURSE TOKEN MUST NEVER COLLIDE WITH A
// GRADE TOKEN. They share a namespace. GRADE_TOKENS in config/geography.js
// owns "prek", "k", "1st".."12th"; nothing here may use those. The assertion
// at the bottom of this file enforces it at import time rather than letting
// you find out when a layout silently overwrites another.
//
// ===========================================================================
// WHAT TO CONFIRM BEFORE SEEDING CONTENT AGAINST THIS
// ===========================================================================
// Virginia rewrote its history sequence in 2023, and the middle grades MOVED:
//
//     2015 standards          2023 standards
//     6th  US History I       6th  US History: 1865 to the Present
//     7th  US History II      7th  Civics and Economics
//     8th  Civics & Economics 8th  World Geography
//
// This file follows the 2023 sequence. Divisions transitioned on a staggered
// timeline, so confirm which set your first partner schools are teaching
// before recording a year of 7th-grade Civics for a class still doing US
// History II.
//
// Science: the 2018 K-Physics standards are unchanged, but the Board approved
// expanded high-school science standards in December 2025. The COURSE LIST
// below is unaffected — that's what these are, and Biology I is still
// Biology I — but the topics inside each course may need review.

import { GRADE_TOKENS } from "../config/geography";

// ---------------------------------------------------------------------------
// ENROLLMENT MODE — the actual product decision, expressed as data
// ---------------------------------------------------------------------------
// "fixed"  : the grade determines the course. Nobody chooses anything; the
//            portal resolves it silently. K-8 everything, plus 9-12 history.
// "choice" : the student picks from a list. 9-12 science and math.
//
// This is what decides whether a chooser appears, and it's also the honest
// answer to "should we sell per subject?" — a subject worth selling
// separately is a subject someone enrolls in separately.
export const ENROLLMENT = {
  FIXED: "fixed",
  CHOICE: "choice",
};

// ---------------------------------------------------------------------------
// PLACEMENT — running both standards at once
// ---------------------------------------------------------------------------
// Virginia's 2023 rewrite moved history down a grade in the middle school,
// and divisions are transitioning on their own timetables. So right now a
// 7th-grade social studies teacher in Virginia is teaching EITHER Civics and
// Economics (2023) OR US History: 1865 to the Present (2015), depending
// entirely on which division they work in. Picking one and hard-coding it
// means being wrong for roughly half the state.
//
// `placement` records where a course sits under each set:
//
//     placement: { 2023: "7th", 2015: "8th" }
//
// A grade with two entries gets a chooser, each labelled with its standards
// year. A grade whose entries all agree gets no chooser at all — that's every
// grade from 9 up, where the two sets are identical.
//
// WHEN THE TRANSITION FINISHES: delete the 2015 keys. Courses left with no
// placement disappear from the chooser on their own; nothing else changes,
// and any layout already built stays reachable by its token.
export const STANDARDS = {
  CURRENT: "2023",
  LEGACY: "2015",
};

// Newest first — decides the order options appear in the chooser, so the
// current standards are the top option everywhere.
const STANDARDS_ORDER = [STANDARDS.CURRENT, STANDARDS.LEGACY];

// ---------------------------------------------------------------------------
// VIRGINIA
// ---------------------------------------------------------------------------
// `grades` is where a course TYPICALLY sits. It orders the chooser and
// preselects a sensible default — it is not a restriction. An 8th grader in
// Algebra 1 and an 11th grader in Algebra 1 are both real students, and
// telling either of them their course doesn't exist is how you lose them.
const VA_COURSES = {
  socialstudies: [
    // ---- The transition grades. See PLACEMENT above. ----
    //
    // Note what ISN'T here: a duplicate. "United States History: 1865 to the
    // Present" is one course that sits in grade 6 under the 2023 standards
    // and grade 7 under the 2015 ones. Same standards, same topics, same
    // songs — a year apart. So it's one token, one layout, built once, and
    // both students land on it.
    //
    // The transition therefore costs one extra course (US History to 1865)
    // and nothing else.
    {
      token: "us-history-to-1865",
      title: "United States History to 1865",
      placement: { 2015: "6th" },
    },
    {
      token: "us-history-1865",
      title: "United States History: 1865 to the Present",
      placement: { 2023: "6th", 2015: "7th" },
    },
    {
      token: "civics-economics",
      title: "Civics and Economics",
      placement: { 2023: "7th", 2015: "8th" },
    },
    {
      token: "world-geography",
      title: "World Geography",
      placement: { 2023: "8th" },
    },

    // ---- 9-12: both sets agree, so no chooser appears. ----
    {
      token: "world-history-1",
      title: "World History and Geography to 1500 A.D.",
      placement: { 2023: "9th", 2015: "9th" },
    },
    {
      token: "world-history-2",
      title: "World History and Geography: 1500 A.D. to the Present",
      placement: { 2023: "10th", 2015: "10th" },
    },
    {
      token: "va-us-history",
      title: "Virginia and United States History",
      placement: { 2023: "11th", 2015: "11th" },
    },
    {
      token: "va-us-government",
      title: "Virginia and United States Government",
      placement: { 2023: "12th", 2015: "12th" },
    },
  ],

  science: [
    { token: "science-6", title: "Science 6", grades: ["6th"] },
    { token: "life-science", title: "Life Science", grades: ["7th"] },
    { token: "physical-science", title: "Physical Science", grades: ["8th", "9th"] },

    { token: "biology-1", title: "Biology I", grades: ["9th", "10th"] },
    { token: "biology-2-survey", title: "Biology II: Advanced Survey of Biology Topics", grades: ["11th", "12th"] },
    { token: "biology-2-anatomy", title: "Biology II: Anatomy and Physiology", grades: ["11th", "12th"] },
    { token: "biology-2-ecology", title: "Biology II: Ecology", grades: ["11th", "12th"] },
    { token: "biology-2-genetics", title: "Biology II: Genetics", grades: ["11th", "12th"] },

    { token: "chemistry-1", title: "Chemistry I", grades: ["10th", "11th"] },
    { token: "chemistry-2-survey", title: "Chemistry II: Advanced Survey of Chemistry Topics", grades: ["11th", "12th"] },

    { token: "earth-science-1", title: "Earth Science I", grades: ["9th", "10th"] },
    { token: "earth-science-2-survey", title: "Earth Science II: Advanced Survey of Earth Science Topics", grades: ["11th", "12th"] },
    { token: "earth-science-2-astronomy", title: "Earth Science II: Astronomy", grades: ["11th", "12th"] },
    { token: "earth-science-2-geology", title: "Earth Science II: Geology", grades: ["11th", "12th"] },
    { token: "earth-science-2-oceanography", title: "Earth Science II: Oceanography", grades: ["11th", "12th"] },

    { token: "environmental-science", title: "Environmental Science", grades: ["11th", "12th"] },

    { token: "physics-1", title: "Physics I", grades: ["11th", "12th"] },
    { token: "physics-2-survey", title: "Physics II: Advanced Survey of Physics Topics", grades: ["12th"] },
  ],

  math: [
    { token: "math-6", title: "Mathematics 6", grades: ["6th"] },
    { token: "math-7", title: "Mathematics 7", grades: ["7th"] },
    { token: "math-8", title: "Mathematics 8", grades: ["8th"] },

    { token: "algebra-1", title: "Algebra 1", grades: ["8th", "9th"] },
    { token: "geometry", title: "Geometry", grades: ["9th", "10th"] },
    { token: "afda", title: "Algebra, Functions, and Data Analysis", grades: ["10th", "11th"] },
    { token: "algebra-2", title: "Algebra 2", grades: ["10th", "11th"] },
    { token: "trigonometry", title: "Trigonometry", grades: ["11th", "12th"] },
    { token: "computer-math", title: "Computer Mathematics", grades: ["11th", "12th"] },
    { token: "probability-statistics", title: "Probability and Statistics", grades: ["11th", "12th"] },
    { token: "discrete-math", title: "Discrete Mathematics", grades: ["11th", "12th"] },
    { token: "mathematical-analysis", title: "Mathematical Analysis", grades: ["12th"] },
    { token: "data-science", title: "Data Science", grades: ["11th", "12th"] },
  ],

  // "Other" (art, music, health, PE) has no course split in VA — it stays
  // grade-addressed all the way up.
  other: [],
};

// WV is deliberately empty. The wv-topics/ files in this repo are named nc-*
// and several are byte-identical to the NC versions, so there is no WV
// curriculum to enumerate yet. An empty list means "grade-addressed", which
// is the safe default — it can't send anyone to a page that isn't there.
const WV_COURSES = { socialstudies: [], science: [], math: [], other: [] };

// ---------------------------------------------------------------------------
// Normalisation
// ---------------------------------------------------------------------------
// A course declares EITHER `grades` (science, math — no transition to worry
// about) OR `placement` (social studies). Everything downstream reads
// `grades`, so it's derived here rather than written twice. Two hand-written
// lists that have to agree is the failure mode this whole file is trying to
// avoid; there's no reason to reintroduce it one level down.
function normalizeCourse(course) {
  if (course.grades) return { ...course, placement: course.placement || null };

  const placement = course.placement || {};
  const grades = [...new Set(Object.values(placement))];
  return { ...course, grades, placement };
}

function normalizeState(subjects) {
  return Object.fromEntries(
    Object.entries(subjects).map(([subject, list]) => [
      subject,
      list.map(normalizeCourse),
    ])
  );
}

export const COURSE_CATALOG = {
  va: normalizeState(VA_COURSES),
  wv: normalizeState(WV_COURSES),
};

// ---------------------------------------------------------------------------
// Lookups
// ---------------------------------------------------------------------------

const key = (v) => String(v || "").toLowerCase().trim();

/**
 * Which standards year puts this course in this grade.
 *
 * Returns "" when the course has no placement data, or when every set agrees
 * — in both cases there's nothing to disambiguate and a label would be noise.
 * A 9th grader shouldn't see "World History I (2023 standards)" when it's
 * also the 2015 course; that reads as a warning about something.
 */
export function standardsFor(course, grade) {
  if (!course?.placement) return "";

  const years = STANDARDS_ORDER.filter(
    (y) => course.placement[y] === String(grade)
  );

  // Placed identically under both — no distinction worth drawing.
  if (years.length !== 1) return "";
  return years[0];
}

/**
 * What to print next to a course in the chooser: the title, plus the
 * standards year when — and only when — it tells the teacher something.
 */
export function chooserLabel(course, grade) {
  const year = standardsFor(course, grade);
  return year ? `${course.title} (${year} standards)` : course.title;
}

/**
 * Every course defined for a state + subject, unfiltered.
 */
export function coursesFor(state, subject) {
  return COURSE_CATALOG[key(state)]?.[key(subject)] || [];
}

/**
 * The courses a student in this grade would plausibly be enrolled in.
 *
 * Returns [] when the subject is grade-addressed in this state — that empty
 * array is meaningful, not a miss. It means "use the grade token".
 */
export function coursesForGrade(state, grade, subject) {
  const g = String(grade || "");
  const matches = coursesFor(state, subject).filter((c) => c.grades.includes(g));

  // Current standards first. When a 7th-grade teacher sees two options, the
  // one most of them want should be the one they read first.
  return matches.sort((a, b) => {
    const ai = STANDARDS_ORDER.indexOf(standardsFor(a, g));
    const bi = STANDARDS_ORDER.indexOf(standardsFor(b, g));
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });
}

/**
 * How this (state, grade, subject) is enrolled — the one call the portal and
 * the plan step should both make.
 *
 * Returns:
 *   { mode: FIXED,  track: "5th",       course: null }   K-5 anything
 *   { mode: FIXED,  track: "world-history-1", course }   9th VA history
 *   { mode: CHOICE, track: null, options: [...] }        10th VA science
 *
 * `track` is what goes in the :grade slot of the portal URL and in the
 * portalLayouts document ID. When mode is CHOICE there is no track until the
 * student picks one.
 */
export function resolveTrack({ state, grade, subject }) {
  const options = coursesForGrade(state, grade, subject);

  if (options.length === 0) {
    return {
      mode: ENROLLMENT.FIXED,
      track: GRADE_TOKENS[grade] || key(grade),
      course: null,
      options: [],
    };
  }

  if (options.length === 1) {
    return {
      mode: ENROLLMENT.FIXED,
      track: options[0].token,
      course: options[0],
      options,
    };
  }

  return { mode: ENROLLMENT.CHOICE, track: null, course: null, options };
}

/**
 * Course metadata from a track token, or null if the token is a grade.
 */
export function courseByToken(state, subject, token) {
  return coursesFor(state, subject).find((c) => c.token === key(token)) || null;
}

/**
 * What to print at the top of the portal page.
 *
 * "10th Grade Science" is wrong when the student is in Chemistry I, and it's
 * the kind of wrong that makes a teacher distrust everything else on the page.
 */
export function trackLabel({ state, subject, track, grade }) {
  const course = courseByToken(state, subject, track);
  if (course) return course.title;
  return grade ? `${grade} Grade` : "";
}

/**
 * Does this state + grade need a course chooser anywhere in its subjects?
 * Cheap way for the signup and dashboard to know whether to ask.
 */
export function gradeUsesCourses(state, grade) {
  return ["science", "math", "socialstudies", "other"].some(
    (s) => coursesForGrade(state, grade, s).length > 0
  );
}

// ---------------------------------------------------------------------------
// Namespace guard
// ---------------------------------------------------------------------------
// Course tokens and grade tokens share the :grade slot in the portal URL and
// the middle segment of every portalLayouts document ID. A collision would
// not throw — it would quietly point two different courses at one layout, and
// you'd find out from a teacher wondering why Algebra 2 shows 10th-grade
// content. Cheaper to fail on import.
const GRADE_TOKEN_SET = new Set(Object.values(GRADE_TOKENS));
const seen = new Set();

Object.entries(COURSE_CATALOG).forEach(([stateKey, subjects]) => {
  Object.entries(subjects).forEach(([subjectKey, list]) => {
    list.forEach((c) => {
      if (GRADE_TOKEN_SET.has(c.token)) {
        throw new Error(
          `Course token "${c.token}" (${stateKey}/${subjectKey}) collides with a grade token. Rename it.`
        );
      }
      const id = `${stateKey}:${subjectKey}:${c.token}`;
      if (seen.has(id)) {
        throw new Error(`Duplicate course token "${c.token}" in ${stateKey}/${subjectKey}.`);
      }
      seen.add(id);
    });
  });
});

export default COURSE_CATALOG;
